// kitchen (ops plan 2026-10-13-kds; parity api-commerce/kds-routing.md, kds/workflows.md W5, W15): what the register
// sends to the location's KDS stations. A KDS station is a printer of kind `kds`; the register routes each send with
// core's kitchen routing exactly as it routes paper tickets (K-R1..K-R9), and records one ticket per station on its own
// stream (`kds_ticket:<ticketId>`). A void reaches every station the order routes to as its own notice. Screens fold the
// station's tickets into cards (core `kitchen/board`): a second send for an open order updates its card, as legacy's
// `reconcileOrder` does. What a cook does to a card (complete, recall, rush, un-rush, tick items) is recorded by the
// screen on the stream of the ticket that opened the card, so every screen of the station folds it. Nothing here is money.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { additive } from '../../define/upcast.ts';
import { DeviceId, EpochMs, KitchenTicketId, LocationId, PrinterId } from '../../primitives/codecs.ts';

const kdsTicket = { aggregate: 'kds_ticket', deviceConsumed: true } as const;

const Text = (max: number) => z.string().max(max);

/** One modifier as the card shows it: the kitchen name when the option has one, and its conversational words. */
export const KitchenTicketOption = z
  .object({
    name: z.string().min(1).max(120),
    count: z.int().min(0).max(999),
    side: z.union([z.literal(1), z.literal(2)]),
    presence: z.enum(['add', 'no', 'only']).nullable(),
    portion: z.enum(['extra', 'lite']).nullable(),
    presentation: z.enum(['side', 'tossed']).nullable(),
    /** Included in the price ("+1" rather than "x1" in the flyout). */
    included: z.boolean(),
  })
  .meta({ id: 'KitchenTicketOption' });

export const KitchenTicketModifierGroup = z
  .object({
    name: z.string().min(1).max(120),
    /** The set's name shows in bold before its options (legacy `show_name_on_kitchen_receipt`). */
    showName: z.boolean(),
    split: z.boolean(),
    splitText: z.enum(['Side', 'Half']),
    options: z.array(KitchenTicketOption).max(200),
  })
  .meta({ id: 'KitchenTicketModifierGroup' });

/**
 * One line on a ticket. The card shows the line's whole quantity (legacy `quantity`); `sendQuantity` is how many this
 * send asks the kitchen for (legacy `additional_quantity`, the paper ticket's quantity).
 */
export const KitchenTicketItem = z
  .object({
    /** The order line (a register line id, or an online order line's index); a later send of the same line merges. */
    lineId: z.string().min(1).max(64),
    quantity: z.number().min(0).max(99_999),
    sendQuantity: z.number().min(0).max(99_999),
    name: z.string().min(1).max(255),
    kitchenName: Text(255).nullable(),
    variantName: Text(255).nullable(),
    modifiers: z.array(KitchenTicketModifierGroup).max(50),
    note: Text(500).nullable(),
  })
  .meta({ id: 'KitchenTicketItem' });

export const KitchenTicketFields = z
  .object({
    ticketId: KitchenTicketId,
    stationId: PrinterId,
    /** The register order, or the online order. */
    orderId: z.uuid(),
    /** The number the kitchen calls the order by. */
    orderNumber: z.string().min(1).max(32),
    online: z.boolean(),
    /** A rushed send (Fire All) makes a card of its own, rushed (legacy `newCard`, `rush`). */
    newCard: z.boolean(),
    rush: z.boolean(),
    diningOptionName: Text(255).nullable(),
    tableName: Text(255).nullable(),
    /** Who sent it (legacy: the signed-in employee on a send, null for an online order). */
    employeeName: Text(255).nullable(),
    /** The tab or customer name; the card shows it in place of the employee when there is one. */
    customerName: Text(255).nullable(),
    items: z.array(KitchenTicketItem).min(1).max(500),
  })
  .meta({ id: 'KitchenTicketFields' });

export const KitchenTicketSent = defineEvent({
  ...kdsTicket,
  name: 'KitchenTicketSent',
  version: 1,
  summary: 'A register sent lines of an order to one KDS station (a send, a fired hold, an accepted online order).',
  payload: KitchenTicketFields.extend({ sentAt: EpochMs }),
  verifiable: ['stationId', 'orderId'],
  merge: 'lifecycle',
});

export const KitchenOrderVoided = defineEvent({
  ...kdsTicket,
  name: 'KitchenOrderVoided',
  version: 1,
  summary: 'An order was voided; the station it routes to marks its card voided (legacy `order-voided`).',
  payload: z.object({ ticketId: KitchenTicketId, stationId: PrinterId, orderId: z.uuid(), orderNumber: z.string().min(1).max(32), voidedAt: EpochMs }),
  verifiable: ['stationId', 'orderId'],
  merge: 'lifecycle',
});

/** Every card fact names its card (the ticket that opened it), its station and its order, so the station's feed carries it. */
const cardFact = { ticketId: KitchenTicketId, stationId: PrinterId, orderId: z.uuid() };
const cardEvent = { ...kdsTicket, verifiable: ['stationId', 'orderId'], merge: 'lifecycle' } as const;

/**
 * One modifier option ticked or unticked on an item (Card Behavior › Check off modifiers; legacy `toggleSingleItem` on
 * `modifiers[group].items[option].isComplete`): the option by its set's index and its index in the set, as sent.
 */
export const KitchenOptionMark = z.object({ group: z.int().min(0).max(99), option: z.int().min(0).max(199), done: z.boolean() }).meta({ id: 'KitchenOptionMark' });

/** An item ticked or unticked; `options` when the cook ticked its modifier options one by one. */
export const KitchenItemMark = z
  .object({ lineId: z.string().min(1).max(64), done: z.boolean(), options: z.array(KitchenOptionMark).max(500).optional() })
  .meta({ id: 'KitchenItemMark' });

export const KitchenCardCompleted = defineEvent({
  ...cardEvent,
  name: 'KitchenCardCompleted',
  version: 1,
  summary: "A cook completed a card (Mark as complete, auto-complete, double-tap); `cleared` for a voided card's Clear Order (legacy `complete`).",
  payload: z.object({ ...cardFact, cleared: z.boolean(), completedAt: EpochMs }),
});

export const KitchenCardRecalled = defineEvent({
  ...cardEvent,
  name: 'KitchenCardRecalled',
  version: 1,
  summary: 'A cook recalled a completed card to the Open tab (legacy `recall`).',
  payload: z.object({ ...cardFact, recalledAt: EpochMs }),
});

export const KitchenCardRushed = defineEvent({
  ...cardEvent,
  name: 'KitchenCardRushed',
  version: 1,
  summary: 'A cook rushed a card to the front (legacy `rush`).',
  payload: z.object({ ...cardFact, rushedAt: EpochMs }),
});

export const KitchenCardUnrushed = defineEvent({
  ...cardEvent,
  name: 'KitchenCardUnrushed',
  version: 1,
  summary: 'A cook undid a rush (legacy `un-rush`).',
  payload: z.object({ ...cardFact, unrushedAt: EpochMs }),
});

export const KitchenItemsMarked = defineEvent({
  ...cardEvent,
  name: 'KitchenItemsMarked',
  // v2: an item mark may carry its modifier options' ticks (Check off modifiers); every v1 payload reads as v2 unchanged.
  version: 2,
  upcastFrom: [additive(1)],
  summary: 'A cook ticked or unticked items on a card (legacy `toggle-item-complete`).',
  payload: z.object({ ...cardFact, items: z.array(KitchenItemMark).min(1).max(500), markedAt: EpochMs }),
});

/**
 * A screen's settings shared with every screen of its station (Settings › General › "Sync settings across devices", on
 * when saved; decision G-K7, legacy `sync-settings`, which nothing ever sent: quirk Q14). It rides a `kds_ticket` stream
 * of its own, which opens no card, so the station's feed carries it as it carries the station's tickets. `settings` is
 * the screen's `kds.*` device settings in their wire form, its KDS printer excepted; a screen applies the keys its build
 * knows and can read.
 */
export const KitchenSettingsShared = defineEvent({
  ...kdsTicket,
  name: 'KitchenSettingsShared',
  version: 1,
  summary: "A KDS screen shared its settings with the station's other screens (Sync settings across devices).",
  payload: z.object({
    ticketId: KitchenTicketId, stationId: PrinterId,
    settings: z.record(z.string().regex(/^kds\.[a-z_.]{1,80}$/), z.unknown()).refine((r) => Object.keys(r).length <= 64, 'at most 64 settings'),
    sharedAt: EpochMs,
  }),
  verifiable: ['stationId'],
  merge: 'lifecycle',
});

export const kitchenTicketEvents = [
  KitchenTicketSent, KitchenOrderVoided, KitchenCardCompleted, KitchenCardRecalled, KitchenCardRushed, KitchenCardUnrushed, KitchenItemsMarked, KitchenSettingsShared,
] as const;

export const KitchenTicketRefused = defineError({
  code: 'kitchen.ticket_refused', httpStatus: 422, message: 'errors.kitchen.ticket_refused', retryable: false,
  details: z.object({ rule: z.string() }),
});

const ticketCommand = {
  stream: { aggregate: 'kds_ticket', id: (input: { ticketId: string }) => input.ticketId },
  output: z.object({ ticketId: KitchenTicketId }),
  permission: { session: 'staff' },
  availability: 'location',
  origin: 'device',
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  errors: [KitchenTicketRefused],
} as const;

export const SendKitchenTicket = defineCommand({
  ...ticketCommand,
  name: 'kitchen.sendTicket',
  summary: "Send an order's lines to one KDS station (the register routes them as it routes paper tickets).",
  input: KitchenTicketFields.extend({ locationId: LocationId }),
  emits: [KitchenTicketSent],
  legacy: ['Ably receive-data on private:kds:location:{loc}:device:{kdsPrinter}'],
});

export const VoidKitchenOrder = defineCommand({
  ...ticketCommand,
  name: 'kitchen.voidOrder',
  summary: 'Tell one KDS station an order was voided.',
  input: z.object({ ticketId: KitchenTicketId, locationId: LocationId, stationId: PrinterId, orderId: z.uuid(), orderNumber: z.string().min(1).max(32) }),
  emits: [KitchenOrderVoided],
  legacy: ['Ably order-voided on private:kds:location:{loc}:device:{kdsPrinter}'],
});

// ---- the station's screens ------------------------------------------------------------------------------------------
// A KDS screen has no one signed in: it acts as itself (device session). Its commands go to the location's node first,
// as every register fact does, and the screen decides them offline with core's ticket aggregate. Only a kitchen display
// holds `kitchen.operate` (KDS-P-01), so a register or handheld acting as itself cannot work a card.
const cardCommand = { ...ticketCommand, permission: 'kitchen.operate' } as const;
const cardInput = { ticketId: KitchenTicketId, locationId: LocationId };

export const CompleteKitchenCard = defineCommand({
  ...cardCommand,
  name: 'kitchen.completeCard',
  summary: 'Complete a card on a KDS screen (Mark as complete, auto-complete, double-tap, Clear Order on a voided card).',
  input: z.object({ ...cardInput, cleared: z.boolean() }),
  emits: [KitchenCardCompleted],
  legacy: ['Ably complete on private:kds:location:{loc}:device:{kdsPrinter}'],
});

export const RecallKitchenCard = defineCommand({
  ...cardCommand,
  name: 'kitchen.recallCard',
  summary: 'Recall a completed card to the Open tab of a KDS screen.',
  input: z.object(cardInput),
  emits: [KitchenCardRecalled],
  legacy: ['Ably recall on private:kds:location:{loc}:device:{kdsPrinter}'],
});

export const RushKitchenCard = defineCommand({
  ...cardCommand,
  name: 'kitchen.rushCard',
  summary: 'Rush a card to the front of a KDS screen.',
  input: z.object(cardInput),
  emits: [KitchenCardRushed],
  legacy: ['Ably rush on private:kds:location:{loc}:device:{kdsPrinter}'],
});

export const UnrushKitchenCard = defineCommand({
  ...cardCommand,
  name: 'kitchen.unrushCard',
  summary: 'Undo a rush on a KDS screen.',
  input: z.object(cardInput),
  emits: [KitchenCardUnrushed],
  legacy: ['Ably un-rush on private:kds:location:{loc}:device:{kdsPrinter}'],
});

export const MarkKitchenItems = defineCommand({
  ...cardCommand,
  name: 'kitchen.markItems',
  summary: "Tick or untick items on a KDS screen's card.",
  input: z.object({ ...cardInput, items: z.array(KitchenItemMark).min(1).max(500) }),
  emits: [KitchenItemsMarked],
  legacy: ['Ably toggle-item-complete on private:kds:location:{loc}:device:{kdsPrinter}'],
});

export const kitchenCardCommands = [CompleteKitchenCard, RecallKitchenCard, RushKitchenCard, UnrushKitchenCard, MarkKitchenItems] as const;

export const ShareKitchenSettings = defineCommand({
  ...cardCommand,
  name: 'kitchen.shareSettings',
  summary: "Share a KDS screen's settings with the other screens of its station (Sync settings across devices).",
  input: z.object({ ticketId: KitchenTicketId, locationId: LocationId, stationId: PrinterId, settings: KitchenSettingsShared.payload.shape.settings }),
  emits: [KitchenSettingsShared],
  legacy: ['Ably sync-settings on private:kds:location:{loc}:device:{kdsPrinter} (never sent: quirk Q14)'],
});

export const kitchenCommands = [SendKitchenTicket, VoidKitchenOrder, ...kitchenCardCommands, ShareKitchenSettings] as const;

// ---- the unlinked page ---------------------------------------------------------------------------------------------
/** A KDS screen of the location that shows a printer (its device setting `kds.station.printer_id`). */
export const KdsPrinterLink = z
  .object({ printerId: PrinterId, deviceId: DeviceId, deviceName: z.string() })
  .meta({ id: 'KdsPrinterLink' });

export const KdsPrinterLinks = defineQuery({
  name: 'kitchen.printerLinks',
  summary: 'The KDS screens of the location that show a printer, so the unlinked page can label a printer "In use by {device}".',
  input: z.object({ locationId: LocationId }),
  output: z.object({ links: z.array(KdsPrinterLink) }),
  // Plan-docs D24 (kds-device-identity-refactor): a printer another live screen records is labelled, never refused.
  permission: { session: 'device' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/devices (the picker reads device_name, which the API never sends)'],
});

export const kitchenQueries = [KdsPrinterLinks] as const;
